import { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { v4 } from 'uuid';

import { PREPARE_UPLOAD } from '@modules/_shared/utils/files/fileQueries';

export type UploadFileFn = (
  file: File,
  callback?: (params: {
    data: null | {
      url: string;
      id: string;
    };
    error: any;
  }) => void,
) => void;

export const useFileUpload = (
  fileType: 'image' | 'file',
): [UploadFileFn, boolean] => {
  const [generateSignedUrl, { loading: generating }] =
    useMutation(PREPARE_UPLOAD);
  const [isUploading, setIsUploading] = useState(false);

  const mutating = generating || isUploading;

  const mutate: UploadFileFn = (file, callback) => {
    generateSignedUrl({
      variables: {
        id: v4(),
        file_type: fileType,
        file_name: file.name,
        mime_type: file.type,
      },
      onCompleted: data => {
        if (data.prepare_upload != null) {
          setIsUploading(true);
          const prepareUploadData = data.prepare_upload;

          fetch(data.prepare_upload.signed_url, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type,
            },
          })
            .then(({ status }) => {
              if (status !== 200) {
                throw new Error('Upload file error');
              }

              callback?.({
                data: { url: prepareUploadData.url, id: prepareUploadData.id },
                error: null,
              });
            })
            .catch(error => {
              callback?.({ data: null, error });
            })
            .finally(() => setIsUploading(false));
        } else {
          // A null `prepare_upload` with no GraphQL error reached neither this
          // branch nor `onError`, so the callback never ran: `uploadFileAsync`
          // and every promise-wrapping caller hung forever with no image, no
          // error and no snackbar. Report the failure instead.
          callback?.({
            data: null,
            error: new Error('Upload failed: no signed URL returned'),
          });
        }
      },
      onError: error => {
        callback?.({ data: null, error });
      },
    });
  };
  return [mutate, mutating];
};

export const uploadFileAsync = (
  uploadFile: UploadFileFn,
  file: File,
): Promise<{ id: string; url: string }> =>
  new Promise((resolve, reject) => {
    uploadFile(file, ({ data, error }) => {
      if (error || !data) {
        reject(error ?? new Error('Upload failed'));
      } else {
        resolve(data);
      }
    });
  });
